import { Header } from "shared/components/BasicHeader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import css from "./styles.module.scss";
import schoolPagePicture from "assets/images/school-background.jpg";
import faqArrow from "assets/images/arrow-down.png";
import { Text } from "shared/components/Text";
import teacherIcon from "assets/icons/schoolPage/teacher-icon.png";
import studentIcon from "assets/icons/schoolPage/student-icon.png";
import packagesIcon from "assets/images/school-packages.png";
import gradientBar from "assets/images/gradient-bar.png";
import cubes from "assets/images/cubes.png";

import { Button } from "shared/components/Button";
import { ReviewItem } from "./ReviewItem";
import i18n from "i18n";
import { Footer } from "shared/components/Footer";
import cn from "classnames";
import { EventsPagesForm } from "features/events/EventsPagesForm";
import { useRef, useState, useEffect, useMemo } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { LoadingOverlay } from "shared/components/LoadingOverlay";
import { getCity, getLocations } from "store/selectors";
import { appStore } from "store";

export const SchoolPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const topRef = useRef();

  const { locations } = appStore.useState(getLocations);
  const { city } = appStore.useState(getCity);
  const siteTitle = locations.cities[city].cityOptions.siteTitle;
  const i18Key = locations.cities[city].cityOptions.i18Key;

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [content, setContent] = useState([]);
  const footerScrollCallback = () => {
    topRef.current.scrollIntoView({ behavior: "smooth" });
    topRef.current.focus();
  };

  const schoolPageContent =
    locations.cities[city].cooperationSection.schoolPage;

  useEffect(() => {
    if (!locations.cities[city].cooperationSection.isHrefEnabled) {
      navigate(`/${city}`);
      return;
    }
    document.title = `${siteTitle} - Schools`;
  }, []);

  useEffect(() => {
    const contentFromTranslations = t(`schoolPage.content.${i18Key}`, {
      returnObjects: true,
    });
    contentFromTranslations.map((el) => {
      el.isAnswerVisible = false;
    });
    setContent(contentFromTranslations);
  }, [i18n.language, city]);

  return (
    <div className={css.SchoolPage}>
      <div className={css.recaptchaContainer} id="recaptchaContainer" />
      {isSubmitLoading && <LoadingOverlay />}
      <Header
        city={city}
        cityName={locations.cities[city]?.cityOptions.title || ""}
      />
      <div className={css.container}>
        <div className={css.flexContainer}>
          <div className={css.schoolPagePicture}>
            <div className={css.schoolPagePictureBlur}>
              <img
                src={schoolPagePicture}
                alt="School page picture"
                className={css.schoolPagePicture}
                ref={topRef}
              />
            </div>
            <div className={css.schoolPagePictureContainer}>
              <Button
                text={t("events.bookingButton")}
                className={css.bookingButton}
                onClick={() => {
                  navigate(`/${city}/booking`);
                }}
              ></Button>
              <Text
                className={css.schoolPagePictureText}
                type="white"
                bold
                secondaryFamily
                size="subTitle"
              >
                {t("schoolPage.pictureTitle")}
              </Text>
            </div>
          </div>
          <div className={css.schoolDescriptionContainer}>
            <Text
              className={css.schoolTitle}
              bold
              secondaryFamily
              alignCenter
              type="white"
              size="sectionTitle"
            >
              {t("aboutSection.title")}
            </Text>
            <Text
              className={css.schoolBody}
              type="white"
              alignCenter
              size="bodyLead"
              bold
            >
              {t("schoolPage.aboutUsSchoolTitle")}
            </Text>
            <Text
              className={css.schoolBody}
              type="white"
              alignCenter
              size="body"
            >
              {t("schoolPage.aboutUsSchoolBody")}
            </Text>
            <Text
              type="white"
              className={css.schoolBody}
              alignCenter
              size="bodyLead"
              bold
            >
              {t("schoolPage.schoolDescriptionTitle")}
            </Text>
            <Text
              type="white"
              className={css.schoolBody}
              alignCenter
              size="body"
            >
              {t("schoolPage.schoolDescriptionBody")}
            </Text>
          </div>
          <div className={css.imageContainer}>
            {schoolPageContent.schoolOfferImages &&
              schoolPageContent.schoolOfferImages.map((image, index) => (
                <div key={index} className={css.imageWrapper}>
                  <img
                    src={image.src}
                    alt={image.alt}
                    title={image.title}
                    className={css.image}
                  />
                </div>
              ))}
          </div>
          <div className={css.flexContainerFeatures}>
            {schoolPageContent.features.map((feature, index) => (
              <div
                key={index}
                className={cn(css.features, {
                  [css.featuresReverse]: index % 2,
                })}
              >
                <img
                  src={feature.img}
                  className={css.icons}
                  alt={feature.title}
                />
                <div className={css.featureDescriptionContainer}>
                  <Text
                    type="white"
                    className={css.schoolBody}
                    bold
                    size="subTitle"
                  >
                    {feature.title}
                  </Text>
                  <Text className={css.schoolBody} type="white" size="body">
                    {feature.content}
                  </Text>
                </div>
              </div>
            ))}
          </div>
          <div className={css.schoolDescriptionContainer}>
            <Text
              className={css.schoolBody}
              type="white"
              alignCenter
              size="caption"
              bold
            >
              {t(`schoolPage.updateDate`, {
                updateDate: schoolPageContent.updateDate,
              })}
            </Text>
            <img
              src={gradientBar}
              alt="gradient Bar"
              className={css.gradientBar}
            />
            <Text
              className={css.schoolBody}
              type="white"
              alignCenter
              size="body"
            >
              {t(`schoolPage.afterFeaturesDescription`)}
            </Text>
          </div>
          {schoolPageContent.doesCityHavePackages && (
            <div className={css.schoolDescriptionContainer}>
              <Text
                className={css.schoolBodyWithShadow}
                type="white"
                alignCenter
                size="bodyLead"
                bold
              >
                {t("schoolPage.packagesTitle")}
              </Text>
              <div className={css.packagesRow}>
                <img
                  src={packagesIcon}
                  className={css.packageIcon}
                  alt="Package icon"
                />
                <div className={css.schoolDescriptionContainer}>
                  <Text
                    className={css.schoolBody}
                    type="white"
                    alignCenter
                    size="body"
                  >
                    {t("schoolPage.packageSalty")}
                  </Text>
                  <Text
                    className={css.schoolBody}
                    type="white"
                    alignCenter
                    size="body"
                  >
                    {t("schoolPage.packageSweet")}
                  </Text>
                </div>
              </div>
            </div>
          )}
          <div className={css.schoolDescriptionContainer}>
            <Text
              className={css.schoolTitle}
              bold
              secondaryFamily
              alignCenter
              type="white"
              size="sectionTitle"
            >
              {t("schoolPage.whyVisitUsTitle")}
            </Text>
            <div className={css.whyVisitContainer}>
              <div className={css.whyVisitColumn}>
                <img
                  src={studentIcon}
                  className={css.humansIcons}
                  alt="Student icon"
                />
                <Text
                  className={css.schoolBody}
                  alignCenter
                  type="white"
                  size="bodyLead"
                >
                  {t("schoolPage.whyVisitUsStudentTitle")}
                </Text>
                <Text
                  className={css.schoolBody}
                  alignCenter
                  type="white"
                  size="body"
                >
                  {t("schoolPage.whyVisitUsStudentBody")}
                </Text>
              </div>
              <div className={css.whyVisitColumn}>
                <img
                  src={teacherIcon}
                  className={css.humansIcons}
                  alt="Teacher icon"
                />
                <Text
                  className={css.schoolBody}
                  alignCenter
                  type="white"
                  size="bodyLead"
                >
                  {t("schoolPage.whyVisitUsTeacherTitle")}
                </Text>
                <Text
                  className={css.schoolBody}
                  alignCenter
                  type="white"
                  size="body"
                >
                  {t("schoolPage.whyVisitUsTeacherBody")}
                </Text>
              </div>
            </div>
          </div>
          {schoolPageContent.reviews && (
            <>
              <div>
                <Text
                  className={css.schoolTitle}
                  bold
                  secondaryFamily
                  alignCenter
                  type="white"
                  size="sectionTitle"
                >
                  {t("reviewSection.title")}
                </Text>
              </div>
              <div className={css.reviewsContainer}>
                {schoolPageContent.reviews.map((el) => (
                  <ReviewItem
                    key={el.name}
                    image={el.img}
                    href={el.href}
                    title={el.name}
                  />
                ))}
              </div>
            </>
          )}
          <div className={css.schoolDescriptionContainer}>
            <Text
              className={css.schoolBodyWithShadow}
              bold
              alignCenter
              type="white"
              size="subTitle"
            >
              {t("schoolPage.checkWhatPlay")}
            </Text>
            <a
              href={schoolPageContent.schoolOfferCatalog}
              target="_blank"
              download
            >
              <Button
                text={t("classesPage.downloadCatalog")}
                className={css.catalogButton}
              />
            </a>
          </div>
          <div className={css.faqContent}>
            {content.map((el, i) => {
              return (
                <div key={i} className={css.contentItemContainer}>
                  <button
                    onClick={() => {
                      setContent((prev) => {
                        const newContent = prev.map((el, index) => {
                          if (index === i) {
                            el.isAnswerVisible = !el.isAnswerVisible;
                          }
                          return el;
                        });
                        return newContent;
                      });
                    }}
                    className={cn(css.contentQuestionContainer, {
                      [css.contentQuestionContainerLighter]: i % 2,
                    })}
                  >
                    <Text
                      type="white"
                      className={css.contentQuestion}
                      secondaryFamily
                      bold
                      size="subTitle"
                    >
                      {i + 1}. {el.question}
                    </Text>
                    <img
                      src={faqArrow}
                      alt="faq arrow"
                      className={cn(css.faqArrowImage, {
                        [css.reversed]: el.isAnswerVisible,
                      })}
                    />
                  </button>
                  {el.isAnswerVisible && (
                    <Text type="white" className={css.content} size="body">
                      {el.answer}
                    </Text>
                  )}
                </div>
              );
            })}
          </div>
          <GoogleReCaptchaProvider
            reCaptchaKey={process.env.REACT_APP_EVENTS_RECAPTCHA_KEY}
            container={{
              element: "recaptchaContainer",
              parameters: {
                badge: "bottomleft",
                size: "invisible",
              },
            }}
          >
            <img
              src={gradientBar}
              alt="gradient Bar"
              className={css.gradientBar}
            />
            <EventsPagesForm
              setIsSubmitLoading={setIsSubmitLoading}
              pageType="School"
              city={city}
            />
          </GoogleReCaptchaProvider>
        </div>
      </div>
      <img src={cubes} alt="Colorful cubes" className={css.cubesImage} />
      <Footer scrollCallback={footerScrollCallback} />
    </div>
  );
};
